<template>
  <div class="min-h-screen">
    <DoctorDetailPageRoot v-if="doctorDetails" :doctorDetails="doctorDetails" />
    <div v-else class="flex items-center justify-center py-16 text-gray-500">
      <span role="status" aria-live="polite">正在载入医生资料…</span>
    </div>
  </div>
  
</template>

<script setup>
const route = useRoute()
const router = useRouter()
const { setSEOMeta, setDoctorStructuredData, setLLMOptimizedMeta, setHreflangAlternates, generateSEOUrl } = useSEO()
// Emit hreflang alternates for all locales
try { setHreflangAlternates() } catch {}

var specialty = route.params.specialty
var doctor_name = route.params.slug?.replace(/医生/g, '')
const doctorDetails = useState('doctorDetails', () => null)

// Implement lookup strategy: prefer direct lookup by provider id if provided via query, else try specialty+name, then fallback
let doctorData = null

// Prefer query.id for direct lookup to avoid ES fallbacks
const idParam = (route.query && typeof route.query.id === 'string') ? route.query.id : ''
if (idParam) {
  try {
    console.log(`[Doctor Profile] Attempting ID lookup: id=${idParam}`)
    doctorData = await $fetch(`/api/doctor/getDetails?id=${idParam}`)
    console.log('[Doctor Profile] ID lookup successful')
  } catch (err) {
    console.error('[Doctor Profile] ID lookup failed:', err)
  }
}

if (!doctorData && route.params.slug) {
  // Traditional format: /doctor/specialty/doctor-name
  // Try direct MongoDB lookup first
  try {
    console.log(`[Doctor Profile] Attempting direct lookup: specialty=${specialty}, doctor_name=${doctor_name}`)
    doctorData = await $fetch(`/api/doctor/getDetails?specialty=${specialty}&doctor_name=${doctor_name}`)
    console.log('[Doctor Profile] Direct lookup successful')
  } catch (error) {
    console.log('[Doctor Profile] Direct lookup failed, trying Elasticsearch fallback')
    // Fallback to Elasticsearch with empty doctor_name to trigger search
    try {
      doctorData = await $fetch(`/api/doctor/getDetails?specialty=${specialty}&doctor_name=`)
    } catch (fallbackError) {
      console.error('[Doctor Profile] Both direct lookup and Elasticsearch fallback failed:', fallbackError)
      doctorData = null
    }
  }
} else {
  // Slug format: /doctor/slug-with-npi (e.g., /doctor/wai-ping-chan-dermatology-1033354428)
  // Use slug-based lookup (Elasticsearch)
  try {
    console.log(`[Doctor Profile] Attempting slug lookup: slug=${specialty}`)
    doctorData = await $fetch(`/api/doctor/getDetails?slug=${specialty}`)
  } catch (error) {
    console.error('[Doctor Profile] Slug lookup failed:', error)
    doctorData = null
  }
}

doctorDetails.value = doctorData || null

// Early fallback SEO if data fetch failed
if (!doctorDetails.value) {
  try {
    setSEOMeta({
      title: '纽约医生资料 - 北美好医生 | Chinese Doctors in New York',
      description: '浏览纽约华人医生和诊所信息，包括专科、地址和联系方式。Explore Chinese doctors in New York with specialties, addresses, and contact info.',
      keywords: ['纽约医生', '华人医生', '医生资料', '医生预约', 'New York Chinese doctor'],
      ogImage: '/HYS-logo-icon-06.png',
      locale: 'zh-Hans'
    })
    // Also set simple breadcrumbs for fallback pages
    try {
      const breadcrumbLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://haodrs.com/' },
          { '@type': 'ListItem', position: 2, name: '医生', item: 'https://haodrs.com/doctor' }
        ]
      }
      useHead({ script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(breadcrumbLd) }] })
    } catch {}
  } catch (e) {
    console.error('[Doctor Profile] Failed to set fallback SEO:', e)
  }
}


const knownSpecialties = [
  {"en": "Allergy & Immunology", "zh": "敏感免疫科"},
  {"en": "Cardiothoracic Surgery", "zh": "心胸外科"},
  {"en": "Cardiovascular Disease", "zh": "心血管疾病科"},
  {"en": "Clinical Cardiac Electrophysiology", "zh": "心电生理学科"},
  {"en": "Colon & Rectal Surgery", "zh": "结肠及直肠科"},
  {"en": "Dentistry - Prosthodontics", "zh": "口腔修复科"},
  {"en": "Dermatology", "zh": "皮肤科"},
  {"en": "Dietetics - Medical Nutrition Therapy", "zh": "医学营养科"},
  {"en": "Endocrinology", "zh": "内分泌科"},
  {"en": "Family Practice", "zh": "家庭科"},
  {"en": "Gastroenterology", "zh": "肠胃肝脏科"},
  {"en": "General Practice", "zh": "普通内科"},
  {"en": "General Surgery", "zh": "普通外科"},
  {"en": "Hematology", "zh": "血液科"},
  {"en": "Infectious Disease", "zh": "传染病科"},
  {"en": "Internal Medicine", "zh": "内科"},
  {"en": "Maternal Fetal Medicine", "zh": "高险妇胎儿科"},
  {"en": "Medical Oncology", "zh": "肿瘤科"},
  {"en": "Medical Oncology & Hematology", "zh": "肿瘤血液科"},
  {"en": "Nephrology", "zh": "肾脏科"},
  {"en": "Neurology", "zh": "神经科"},
  {"en": "Neurosurgery", "zh": "神经外科"},
  {"en": "Nurse Practitioner - Adult Health", "zh": "执业护士 - 成人健康"},
  {"en": "Nurse Practitioner - Family", "zh": "执业护士-家庭"},
  {"en": "Nurse Practitioner - Pediatrics", "zh": "执业护士 - 小儿科"},
  {"en": "Nurse Practitioner - Psychiatric-Mental Health", "zh": "执业护士 - 心理科"},
  {"en": "Obstetrics & Gynecology", "zh": "妇产科"},
  {"en": "Ophthalmology", "zh": "眼科"},
  {"en": "Orthopedic Surgery", "zh": "骨骼外科"},
  {"en": "Otolaryngology", "zh": "耳鼻喉科"},
  {"en": "Pain Management", "zh": "疼痛医学科"},
  {"en": "Pediatric Neurology", "zh": "儿童神经科"},
  {"en": "Pediatrics", "zh": "小儿科"},
  {"en": "Physical Medicine & Rehabilitation", "zh": "复健科"},
  {"en": "Plastic & Reconstructive Surgery", "zh": "美容整形外科"},
  {"en": "Podiatry", "zh": "脚科"},
  {"en": "Psychiatry", "zh": "精神科"},
  {"en": "Psychiatry and Neurology", "zh": "精神与神经科"},
  {"en": "Pulmonary Disease", "zh": "胸肺科"},
  {"en": "Radiation Oncology", "zh": "放射肿瘤科"},
  {"en": "Rheumatology", "zh": "风湿病科"},
  {"en": "Speech Language Pathology", "zh": "语言治疗科"},
  {"en": "Urology", "zh": "泌尿科"},
  {"en": "Vascular Neurology", "zh": "血管神经科"},
  {"en": "Vascular Surgery", "zh": "血管外科"},
  {"en": "Acupuncture", "zh": "针灸科"},
  {"en": "Anesthesiology", "zh": "麻醉科"},
  {"en": "Audiology", "zh": "听力科"},
  {"en": "Chiropractic Medicine", "zh": "脊椎神经科"},
  {"en": "Dentistry - Endodontics", "zh": "牙髓病科"},
  {"en": "Dentistry - General", "zh": "牙科"},
  {"en": "Dentistry - Oral & Maxillofacial Surgery", "zh": "口腔颌面外科"},
  {"en": "Dentistry - Pediatrics", "zh": "儿童牙科"},
  {"en": "Marriage & Family Therapy", "zh": "婚姻家庭科"},
  {"en": "Nuclear Medicine", "zh": "核医学科"},
  {"en": "Nurse Practitioner", "zh": "执业护士"},
  {"en": "Occupational Therapy", "zh": "职能治疗科"},
  {"en": "Optometry", "zh": "验光科"},
  {"en": "Physical Therapy", "zh": "物理治疗科"},
  {"en": "Physician Assistant", "zh": "助理医师"},
  {"en": "Psychology", "zh": "心理科"},
  {"en": "Psychotherapy", "zh": "心理治疗科"},
  {"en": "Radiology - Diagnostic", "zh": "放射科 - 诊断"}
]

// Check if the specialty is known in either English or Chinese (only for traditional format)
const isTraditionalFormat = !!route.params.slug
const isSpecialtyKnown = isTraditionalFormat ? knownSpecialties.some(({ en, zh }) => en === specialty || zh === specialty) : false
const isDoctorNameEmpty = doctor_name === '' || !doctor_name

onBeforeMount(() => {
    // Only redirect if it's traditional format with known specialty but empty doctor name
    if (isTraditionalFormat && isSpecialtyKnown && isDoctorNameEmpty) {
        return router.replace(`/specialty/${specialty}`)
    }
})

// Enhanced SEO with AIGC-powered content and structured data with error handling
if (doctorDetails.value?.provider) {
  const provider = doctorDetails.value.provider
  const facility = doctorDetails.value.facilities?.[0]
  const aigcContent = provider.aigc_v3?.chat_response
  
  // Safe data extraction with fallbacks
  const doctorName = provider.cn_name || provider.en_firstname + ' ' + provider.en_lastname || '医生'
  const englishName = provider.en_firstname && provider.en_lastname ? 
    `${provider.en_firstname} ${provider.en_lastname}` : 
    (provider.en_firstname || provider.en_lastname || '')
  const specialtyChinese = provider.cn_specialty_1 || provider.en_specialty_1 || '专科'
  const specialtyEnglish = provider.en_specialty_1 || provider.cn_specialty_1 || 'Specialty'
  const facilityAddress = facility?.cn_full_address || facility?.en_full_address || '纽约'
  const phoneNumber = facility?.telephone_tracking_hys_1 || facility?.telephone || ''
  const npiNumber = facility?.npi || ''
  
  // Prioritize AIGC SEO content over template content with safe fallbacks
  const metaTitle = `${doctorName}医师 | 纽约${specialtyChinese}医生`
  const metaDescription = aigcContent?.seo_metadescription_chinese || 
    `${doctorName}${englishName ? `(${englishName})` : ''}是纽约${specialtyChinese}医生, 诊所位于${facilityAddress}, ${phoneNumber ? `预约挂号电话${phoneNumber}, ` : ''}${npiNumber ? `NPI执照号${npiNumber}。` : ''}接受大部分美国医疗保险。`

  // Use AIGC keywords if available, otherwise fallback to basic keywords
  const baseKeywords = [
    doctorName,
    specialtyChinese,
    '纽约医生',
    '华人医生',
    '医疗保险',
    '预约挂号'
  ]

  // Add English names if available
  if (englishName) {
    baseKeywords.push(englishName)
  }
  if (specialtyEnglish && specialtyEnglish !== specialtyChinese) {
    baseKeywords.push(specialtyEnglish)
  }

  const seoKeywords = aigcContent?.seo_keywords_chinese?.length ? 
    [...aigcContent.seo_keywords_chinese, ...baseKeywords] : baseKeywords

  // Compute preferred canonical URL (prefer slug-only form if available)
  let canonicalUrl
  try {
    const rc = useRuntimeConfig()
    // @ts-ignore - runtimeConfig may not have typed 'site'
    const baseUrl = (rc['site'] && rc['site']['url']) ? rc['site']['url'] : (rc.public && rc.public['siteUrl'] ? rc.public['siteUrl'] : 'https://haodrs.com')
    const slugFromProvider = provider.slug_cn || provider.slug || ''
    if (!isTraditionalFormat && typeof route.params.specialty === 'string') {
      // Already slug-only route, keep current full URL
      canonicalUrl = `${baseUrl}${route.path}`
    } else if (slugFromProvider) {
      canonicalUrl = `${baseUrl}/doctor/${slugFromProvider}`
    } else if (englishName || specialtyEnglish) {
      const npi = facility?.npi ? String(facility.npi) : ''
      const genSlug = generateSEOUrl(`${englishName || doctorName} ${specialtyEnglish || ''} ${npi}`.trim())
      canonicalUrl = `${baseUrl}/doctor/${genSlug}`
    }
  } catch {}

  // Set comprehensive SEO meta with error handling
  try {
    setSEOMeta({
      title: metaTitle,
      description: metaDescription,
      keywords: seoKeywords,
      ogImage: '/HYS-logo-icon-06.png',
      author: doctorName,
      locale: 'zh-Hans',
      ...(canonicalUrl ? { canonicalUrl } : {})
    })
  } catch (seoError) {
    console.error('[Doctor Detail Page] Failed to set SEO meta:', seoError)
    // Don't throw - allow page to continue loading
  }

  // Set healthcare-specific structured data with error handling
  try {
    const structuredData = {
      '@type': 'Physician',
      name: doctorName,
      description: metaDescription,
      image: '/HYS-logo-icon-06.png',
      medicalSpecialty: [specialtyChinese, specialtyEnglish].filter(Boolean),
      memberOf: {
        '@type': 'MedicalOrganization',
        name: '北美好医生'
      },
      acceptsReservations: true,
      paymentAccepted: ['Insurance', 'Cash', 'Credit Card'],
      priceRange: '$$',
      availableLanguage: ['Chinese', 'English']
    }

    // Add optional fields only if they exist
    if (englishName) {
      structuredData.alternateName = englishName
    }
    if (phoneNumber) {
      structuredData.telephone = phoneNumber
      structuredData.memberOf.telephone = phoneNumber
    }
    if (facility?.contact_email) {
      structuredData.email = facility.contact_email
    }
    if (facility?.en_full_address) {
      structuredData.address = {
        '@type': 'PostalAddress',
        streetAddress: facility.en_full_address.split(',')[0] || '',
        addressLocality: facility.city || 'New York',
        addressRegion: facility.state || 'NY',
        postalCode: facility.zip_code || '',
        addressCountry: 'US'
      }
    }

    setDoctorStructuredData(structuredData)

    // Add BreadcrumbList JSON-LD
    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://haodrs.com/' },
        { '@type': 'ListItem', position: 2, name: `${specialtyChinese}`, item: `https://haodrs.com/specialty/${specialtyChinese}` },
        { '@type': 'ListItem', position: 3, name: `${doctorName}`, item: `https://haodrs.com${route.path}` }
      ]
    }
    useHead({ script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(breadcrumbLd) }] })
  } catch (structuredDataError) {
    console.error('[Doctor Detail Page] Failed to set structured data:', structuredDataError)
    // Don't throw - allow page to continue loading
  }

  // Set LLM-optimized metadata with AIGC-enhanced content and error handling
  try {
    const llmKeyTerms = aigcContent?.seo_keywords_chinese?.length ? 
      [...aigcContent.seo_keywords_chinese, doctorName] :
      [
        doctorName,
        specialtyChinese,
        '纽约医生',
        '华人医疗',
        '医生预约'
      ]

    setLLMOptimizedMeta({
      contentType: 'doctor-profile',
      primaryTopic: `${specialtyChinese}医生资料`,
      keyTerms: llmKeyTerms,
      contentSummary: aigcContent?.long_brief_chinese || 
        `${doctorName}医师的完整资料，包括专业背景、诊所信息、联系方式和预约详情`,
      medicalSpecialties: [specialtyChinese, specialtyEnglish].filter(Boolean),
      targetAudience: `需要${specialtyChinese}医疗服务的华人患者`,
      // Include AIGC services and conditions for enhanced LLM understanding
      services: aigcContent?.common_services_provided?.map(s => s.service).filter(Boolean) || [],
      conditions: aigcContent?.common_treated_conditions?.map(c => c.condition).filter(Boolean) || []
    })
  } catch (llmError) {
    console.error('[Doctor Detail Page] Failed to set LLM meta:', llmError)
    // Don't throw - allow page to continue loading
  }
}

</script>